/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import * as React from 'react';
import { type Map } from 'mapbox-gl';
import { useFiltersState } from '../../FiltersContext';

interface UseSearchOnMapMoveParams {
    mapInstance: Map | null;
    disableFitBounds?: () => void;
    /** When true on moveend, skip running search and clear the ref (used after programmatic fitBounds). */
    skipNextMoveEndRef?: React.MutableRefObject<boolean>;
}

export function useSearchOnMapMove({
    mapInstance,
    disableFitBounds,
    skipNextMoveEndRef,
}: UseSearchOnMapMoveParams) {
    const [, setFilters] = useFiltersState();
    const skipNextMoveEndAfterResizeRef = React.useRef(false);

    const searchBounds = React.useCallback(() => {
        if (!mapInstance) return;

        // Skip search when moveend was caused by programmatic fitBounds
        if (skipNextMoveEndRef?.current) {
            skipNextMoveEndRef.current = false;
            return;
        }

        // Skip search when moveend was caused by a map resize
        if (skipNextMoveEndAfterResizeRef.current) {
            skipNextMoveEndAfterResizeRef.current = false;
            return;
        }

        // Disable fit bounds for this search result
        disableFitBounds?.();

        const bounds = mapInstance.getBounds();

        if (bounds) {
            const rawNE = bounds.getNorthEast();
            const rawSW = bounds.getSouthWest();
            const lngSpan = rawNE.lng - rawSW.lng;

            const northEast = rawNE.wrap();
            const southWest = rawSW.wrap();
            const lat_ne = northEast.lat;
            const lat_sw = southWest.lat;

            // When the viewport spans >= 360° of longitude (the full world is visible),
            // wrapping each corner independently collapses the box into a tiny sliver.
            // Clamp to the full longitude range instead.
            const lng_ne = lngSpan >= 360 ? 180 : northEast.lng;
            const lng_sw = lngSpan >= 360 ? -180 : southWest.lng;

            // const { lat, lng } = mapInstance.getCenter().wrap();

            // clear previous criteria and search new bounds only
            setFilters({
                latNe: lat_ne,
                latSw: lat_sw,
                lngNe: lng_ne,
                lngSw: lng_sw,
                location: null,
                locationId: null,
                lat: null,
                lng: null,
                hoveredCardId: null,
            });
        }
    }, [mapInstance, setFilters, skipNextMoveEndRef, disableFitBounds]);

    React.useEffect(() => {
        if (!mapInstance) return;

        mapInstance.on('moveend', searchBounds);

        return () => {
            mapInstance.off('moveend', searchBounds);
        };
    }, [mapInstance, searchBounds]);

    React.useEffect(() => {
        if (!mapInstance) return;

        const handleResize = () => {
            skipNextMoveEndAfterResizeRef.current = true;
        };

        mapInstance.on('resize', handleResize);

        return () => {
            mapInstance.off('resize', handleResize);
        };
    }, [mapInstance]);
}

// cSpell:ignore moveend
